/**
 * F-170 (B4.1) — Product card for `ProfileFeed`.
 *
 * "Додати в кошик" resolves (or opens) the per-specialist `Cart`, then POSTs
 * the item. Stock indicator only renders when the backend reported a finite
 * `stock` value (null = unlimited per ProductOffering model contract).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon } from '@/components/atoms';
import { toast } from '@/components/atoms/Toast';
import { useHapticFeedback } from '@/hooks';
import { useCartStore } from '@/store';
import { carts as cartsApi } from '@/api';
import { resolveMediaUrl } from '@/utils/media';
import { logger } from '@/utils/logger';
import type { ProductOffering } from '@/types/domain';
import styles from './ProductCard.module.css';

export interface ProductCardProps {
  product: ProductOffering;
  /** SpecialistProfile.user_id from the parent profile. Required so the
   *  cart resolver can open / hit the correct per-specialist cart without
   *  another lookup. */
  specialistId: number | string;
}

function formatPrice(value: number, isFrom: boolean): string {
  const num = Number(value).toFixed(2);
  return isFrom ? `від ${num} €` : `${num} €`;
}

export function ProductCard({ product, specialistId }: ProductCardProps): JSX.Element {
  const navigate = useNavigate();
  const haptic = useHapticFeedback();
  // F-180 C13: bump the global cart count when this is the first item.
  const cartCount = useCartStore((s) => s.openCartsCount);
  const setCartCount = useCartStore((s) => s.setCount);
  const [busy, setBusy] = useState(false);
  const outOfStock = product.stock != null && product.stock <= 0;

  const handleAdd = async (): Promise<void> => {
    if (outOfStock || busy) return;
    setBusy(true);
    try {
      // Open / resolve the per-specialist cart, then add the item. The
      // backend `POST /carts/` is idempotent for (client, specialist,
      // status='open') so callers don't need to track cart IDs locally.
      const cart = await cartsApi.openCart({ id: Number(specialistId) || undefined, slug: String(specialistId) });
      await cartsApi.addItem(cart.id, product.id, 1);
      // F-180 C8: confirm the silent tap with a soft haptic notification.
      haptic.notification('success');
      // F-180 C13: keep the NavBar cart pill in sync — make sure the
      // count is at least 1 so the icon surfaces immediately.
      if (cartCount === 0) setCartCount(1);
      toast.success('Додано в кошик');
    } catch (err) {
      logger.warn('addToCart failed', err);
      toast.error('Не вдалось додати в кошик');
    } finally {
      setBusy(false);
    }
  };

  const handleOpenCart = (): void => {
    navigate(`/cart/${specialistId}`);
  };

  return (
    <article className={styles.card}>
      {product.image_url ? (
        <img
          src={resolveMediaUrl(product.image_url)}
          alt={product.title}
          className={styles.image}
          loading="lazy"
        />
      ) : (
        <div className={styles.imagePlaceholder} aria-hidden>
          <Icon name="package" size={32} />
        </div>
      )}
      <div className={styles.body}>
        <div className={styles.head}>
          <span className={styles.title}>{product.title}</span>
          <span className={styles.price}>{formatPrice(product.price_eur, product.price_is_from)}</span>
        </div>
        {product.description ? <p className={styles.description}>{product.description}</p> : null}
        {product.stock != null ? (
          <div className={styles.stock} data-low={product.stock <= 3 ? 'true' : undefined}>
            {outOfStock
              ? 'Немає в наявності'
              : product.stock === 1
                ? 'Залишилась 1 шт.'
                : `В наявності: ${product.stock} шт.`}
          </div>
        ) : null}
        <div className={styles.actions}>
          <Button variant="ghost" onClick={handleOpenCart}>
            До кошика
          </Button>
          <Button onClick={handleAdd} disabled={outOfStock || busy}>
            {outOfStock ? 'Немає' : 'Додати в кошик'}
          </Button>
        </div>
      </div>
    </article>
  );
}
